// What each Home number counts, in words, kept apart from the screen so the
// wording and the links can be tested (CMS review rania-01 to rania-06,
// georges-02, georges-20). Every number on Home uses one definition, said
// once under the tiles (DEFINITION): one per application however many
// people travel, by the day it was submitted, its money the Total Cases
// shows, test accounts hidden or not by the one switch Cases uses. With the
// same dates and switch, each tile equals the list its link opens.

import { casesLink, dayWords } from "src/pages/cases/listParams";

export const DEFINITION =
  "One per application, however many people travel, by the day it was submitted. Money is each application’s Total, as in Cases.";

// "test accounts included" / "test accounts hidden", as the switch is set.
export const testWords = (hidden) => (hidden ? "test accounts hidden" : "test accounts included");

// The period's words: "this week (26 Oct 2026 – 1 Nov 2026)", "all time".
export function periodWords(label, from, to) {
  if (!from && !to) return "all time";
  const span = from && to ? (from === to ? dayWords(from) : `${dayWords(from)} – ${dayWords(to)}`) : from ? `from ${dayWords(from)}` : `until ${dayWords(to)}`;
  return label ? `${label.toLowerCase()} (${span})` : span;
}

// Applications counted like Cases, one per application, when the API says
// so (per: "application", or the older "applications"); an older API counts
// every traveller, and the screen says so.
export function applicationCounts(requests) {
  if (requests?.per === "application") return { total: Number(requests.total) || 0, status: requests.status || {}, perApplication: true };
  const apps = requests?.applications;
  if (apps && typeof apps.total === "number") return { total: apps.total, status: apps.status || {}, perApplication: true };
  return { total: Number(requests?.total) || 0, status: requests?.status || {}, perApplication: false };
}

// The order value worth showing from an older API: what was submitted,
// paid or not. Drafts (incomplete) were never sent and cancelled ones won't
// be paid, so both are left out.
export function orderValue(requests) {
  const total = Number(requests?.totalEarnings) || 0;
  const by = requests?.earningsByStatus || {};
  return Math.max(0, total - (Number(by.incomplete) || 0) - (Number(by.cancelled) || 0));
}

// Money, the same way on Home and Money (georges-04): whole dollars as
// "$5,430", anything with cents as "$45,279.10".
export function amount(value) {
  const n = Number(value || 0);
  const whole = Math.abs(n - Math.round(n)) < 0.005;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }).format(whole ? Math.round(n) : n);
}

// The two money tiles: Paid (every payment, drafts and cancelled ones too:
// rania-03) and Not paid yet (submitted, not paid; drafts and cancelled
// ones owe nothing; georges-15). An older API gives one figure.
// `money` formats an amount ("$1,230").
export function moneyTiles(requests, money = String) {
  const m = requests?.money;
  if (m && typeof m.paid === "number") {
    const refunds = m.paid_cancelled?.count || 0;
    return {
      paid: { value: m.paid, note: "Every payment, drafts and cancelled ones too." },
      owed: { value: m.owed, note: `${m.owed_count === 1 ? "1 submitted application" : `${m.owed_count} submitted applications`}, not paid. Drafts and cancelled ones owe nothing.` },
      refunds: refunds ? { count: refunds, amount: m.paid_cancelled.amount } : null,
      drafts: m.paid_drafts?.count || 0,
    };
  }
  if (typeof requests?.paidEarnings === "number" && typeof requests?.unpaidEarnings === "number") {
    return {
      paid: { value: requests.paidEarnings, note: "Submitted applications only: drafts and cancelled left out." },
      owed: { value: requests.unpaidEarnings, note: "Submitted, not paid. Drafts and cancelled ones owe nothing." },
      refunds: null,
      drafts: 0,
    };
  }
  return { paid: null, owed: { value: orderValue(requests), note: "Submitted applications, paid or not. This server doesn’t split paid from not paid yet." }, refunds: null, drafts: 0 };
}

// The lead tile: Cases' own Needs action count under the same dates and
// switch. A count over no applications at all (dates with none) is 0, not
// missing (rania-06); only a server that doesn't answer falls back to the
// period's submitted and in-review applications, said as such.
export function casesTile({ cases, status }) {
  if (cases?.counts) return { label: "Needs action", value: Number(cases.counts.needs_action) || 0, note: "Open cases: submitted or in review.", link: "Open them in Cases" };
  return {
    label: "Needs action",
    value: Number(status?.pending || 0) + Number(status?.under_review || 0),
    note: "Submitted or in review, from the numbers above.",
    link: "Open them in Cases",
  };
}

// Whether the server keeps owners (Cases answers a list of them, empty or
// not): only then are Unassigned and the team counted.
export const ownersKept = (cases) => Array.isArray(cases?.owners);

// From started to approved, each step with its share of Started and of the
// step before it (rania-07: "Paid ÷ Submitted").
export function funnelSteps(funnel) {
  if (!funnel) return [];
  const steps = [
    { key: "started", label: "Started", value: Number(funnel.started) || 0 },
    { key: "submitted", label: "Submitted", value: Number(funnel.submitted) || 0 },
    { key: "paid", label: "Paid", value: Number(funnel.paid) || 0 },
    { key: "approved", label: "Approved", value: Number(funnel.approved) || 0 },
  ];
  const pct = (a, b) => (b ? Math.round((a / b) * 100) : null);
  return steps.map((step, i) => ({
    ...step,
    ofStarted: i ? pct(step.value, steps[0].value) : null,
    ofPrevious: i > 1 ? pct(step.value, steps[i - 1].value) : null,
    previous: i > 1 ? steps[i - 1].label : null,
  }));
}

// The settings a link carries so its list counts what the tile counted.
export const scopeLink = ({ from, to, hideTest }, extra = {}) => casesLink({ from, to, tests: hideTest ? "0" : "1", ...extra });

// "/money?from=…&to=…&tests=0"
export function moneyLink({ from, to, hideTest }, hash = "", extra = {}) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  params.set("tests", hideTest ? "0" : "1");
  Object.entries(extra).forEach(([key, value]) => value != null && params.set(key, value));
  return `/money?${params.toString()}${hash}`;
}

// "/dashboard?from=…&to=…&tests=0": Home with the same dates and switch.
export function homeLink({ from, to, hideTest }) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  params.set("tests", hideTest ? "0" : "1");
  return `/dashboard?${params.toString()}`;
}
